import type { InfiniteData, QueryClient, QueryKey } from '@tanstack/react-query';

import { queryClient } from '@/app/providers/react-query-provider';

import { CHAT_QUERY_KEYS, type ChatIdentityKey, chatQueryKey } from './query-keys';
import type { GetContextMessagesRes, GetContextsRes } from './v2-types';

type ContextMessagesCache = InfiniteData<GetContextMessagesRes, string | undefined>;

export const setQueryDataKeepingError = <TData>(
  client: QueryClient,
  queryKey: QueryKey,
  updater: (_data: TData | undefined) => TData | undefined,
): void => {
  const query = client.getQueryCache().find({ queryKey, exact: true });
  const failed = query?.state.status === 'error' ? query.state : null;

  client.setQueryData<TData>(queryKey, updater);

  if (query && failed) {
    query.setState({
      status: 'error',
      error: failed.error,
      errorUpdatedAt: failed.errorUpdatedAt,
      errorUpdateCount: failed.errorUpdateCount,
    });
  }
};

export const markContextReadOptimistically = (
  identity: ChatIdentityKey,
  contextId: string,
  upTo?: string,
) => {
  queryClient.setQueriesData<InfiniteData<GetContextsRes, number>>(
    { queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contexts) },
    (data) => {
      if (!data) return data;

      const row = data.pages
        .flatMap((page) => page.rows)
        .find((candidate) => candidate.id === contextId);
      if (!row || row.unreadCount === 0) return data;

      const unread = row.unreadCount;
      return {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          rows: page.rows.map((candidate) =>
            candidate.id === contextId ? { ...candidate, unreadCount: 0 } : candidate,
          ),
          kindUnread: {
            ...page.kindUnread,
            all: Math.max(0, page.kindUnread.all - unread),
            [row.kind]: Math.max(0, page.kindUnread[row.kind] - unread),
          },
          stateCounts: {
            ...page.stateCounts,
            unread: Math.max(0, page.stateCounts.unread - 1),
          },
        })),
      };
    },
  );

  const cutoff = upTo ? new Date(upTo).getTime() : Number.POSITIVE_INFINITY;
  for (const [queryKey] of queryClient.getQueriesData<ContextMessagesCache>({
    queryKey: chatQueryKey(identity, CHAT_QUERY_KEYS.contextMessages, contextId),
  })) {
    setQueryDataKeepingError<ContextMessagesCache>(queryClient, queryKey, (data) =>
      data
        ? {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              entries: page.entries.map((entry) =>
                entry.kind === 'message' && new Date(entry.sentAt).getTime() <= cutoff
                  ? { ...entry, isUnread: false }
                  : entry,
              ),
            })),
          }
        : data,
    );
  }
};
